import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Zap, ArrowLeft, Crown, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMyProfile, planLabel, type PlanTier } from "@/hooks/use-my-profile";
import { StripeEmbeddedCheckoutForm } from "@/components/StripeEmbeddedCheckout";
import { CheckoutDisclaimerGate } from "@/components/checkout-disclaimer-gate";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { hasPaymentsConfigured } from "@/lib/stripe";
import { PLANS } from "@/lib/pricing/plans";

export const Route = createFileRoute("/_authenticated/upgrade")({
  component: UpgradePage,
});

type Tier = {
  id: "pro" | "elite";
  name: string;
  tagline: string;
  monthly: number;
  annual: number; // per-month equivalent when billed annually
  annualTotal: number;
  monthlyPriceId: string;
  annualPriceId: string;
  perks: string[];
  highlight?: boolean;
  icon: typeof Sparkles;
};

const TIERS: Tier[] = [
  {
    id: "pro",
    name: PLANS.analyst.name,
    tagline: "Real-time intelligence for active traders",
    monthly: PLANS.analyst.monthlyUsd,
    annual: +(PLANS.analyst.annualUsd / 12).toFixed(2),
    annualTotal: PLANS.analyst.annualUsd,
    monthlyPriceId: "analyst_monthly",
    annualPriceId: "analyst_annual",
    icon: Sparkles,
    highlight: true,
    perks: [
      "10 pairs real-time",
      "50 ideas per day",
      "Full 11-agent council reasoning",
      "Telegram alerts",
      "Position calculator",
      "7-day free trial",
    ],
  },
  {
    id: "elite",
    name: PLANS.institutional.name,
    tagline: "Research workflows for serious desks",
    monthly: PLANS.institutional.monthlyUsd,
    annual: +(PLANS.institutional.annualUsd / 12).toFixed(2),
    annualTotal: PLANS.institutional.annualUsd,
    monthlyPriceId: "institutional_monthly",
    annualPriceId: "institutional_annual",
    icon: Zap,
    perks: [
      "All 16 pairs + priority XAU/USD",
      "500 ideas per day",
      "API access",
      "Backtesting + walk-forward engine",
      "Downloadable reports",
      "Event-mode coverage (NFP / FOMC / CPI)",
      "Council replay feature",
      "Priority support",
      "14-day free trial",
    ],
  },
];

function UpgradePage() {
  const navigate = useNavigate();
  const { data: profileData } = useMyProfile();
  const currentPlan = (profileData?.profile?.plan ?? "free") as PlanTier;
  const [annual, setAnnual] = useState(true);
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<string>("");
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const openCheckout = (priceId: string, planName: string) => {
    setCheckoutPriceId(priceId);
    setCheckoutPlan(planName);
    setDisclaimerAccepted(false);
  };
  const closeCheckout = () => {
    setCheckoutPriceId(null);
    setDisclaimerAccepted(false);
  };
  const paymentsReady = hasPaymentsConfigured();

  return (
    <main className="flex-1">
      <PaymentTestModeBanner />
      <div className="mx-auto max-w-6xl px-4 py-8 lg:py-12">
        <button
          onClick={() => navigate({ to: "/dashboard" })}
          className="mb-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </button>

        <div className="flex flex-col items-center text-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{
              borderColor:
                currentPlan === "free"
                  ? "color-mix(in oklab, var(--muted-foreground) 40%, transparent)"
                  : "color-mix(in oklab, var(--primary) 40%, transparent)",
              color: currentPlan === "free" ? "var(--muted-foreground)" : "var(--primary)",
              backgroundColor:
                currentPlan === "free"
                  ? "color-mix(in oklab, var(--muted-foreground) 8%, transparent)"
                  : "color-mix(in oklab, var(--primary) 10%, transparent)",
            }}
          >
            You're on {planLabel(currentPlan)} Plan
          </span>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight lg:text-5xl">
            Institutional-grade intelligence. <span className="text-primary">Without the desk.</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Real-time council reasoning, multi-timeframe confluence, and structured market context.
          </p>

          <div className="mt-6 inline-flex items-center rounded-full border border-border/60 bg-card/60 p-1 text-xs font-bold">
            <button
              onClick={() => setAnnual(false)}
              className={cn(
                "rounded-full px-4 py-1.5 transition-colors",
                !annual ? "bg-primary text-primary-foreground" : "text-muted-foreground",
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors",
                annual ? "bg-primary text-primary-foreground" : "text-muted-foreground",
              )}
            >
              Annual
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px]",
                  annual ? "bg-primary-foreground/20 text-primary-foreground" : "bg-buy/20 text-buy",
                )}
              >
                SAVE 2 MONTHS
              </span>
            </button>
          </div>
          {annual && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Save 2 months with annual billing.
            </p>
          )}
        </div>

        <div className="mx-auto mt-8 grid max-w-6xl gap-4 md:grid-cols-3">
          <FreeCard isCurrent={currentPlan === "free"} />
          {TIERS.map((tier) => {
            const isCurrent = currentPlan === tier.id;
            const price = annual ? tier.annual : tier.monthly;
            const priceId = annual ? tier.annualPriceId : tier.monthlyPriceId;
            const Icon = tier.icon;
            const isInstitutional = tier.id === "elite";
            return (
              <Card
                key={tier.id}
                className={cn(
                  "relative flex flex-col gap-4 border-border/60 bg-card/60 p-6",
                  tier.highlight && "border-primary/60 bg-gradient-to-b from-primary/10 to-card/40 shadow-[0_0_40px_-12px_var(--primary)]",
                )}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                    Most popular
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="font-display text-xl font-bold">{tier.name}</h3>
                </div>
                <p className="text-xs text-muted-foreground">{tier.tagline}</p>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">
                    ${annual ? tier.annual.toFixed(annual && tier.annual % 1 ? 2 : 0) : tier.monthly}
                  </span>
                  <span className="text-xs text-muted-foreground">/mo</span>
                  {annual && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-buy">
                      ${tier.annualTotal}/yr
                    </span>
                  )}
                </div>
                <ul className="space-y-2 text-sm">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-buy" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-auto w-full"
                  size="lg"
                  disabled={isCurrent || !paymentsReady}
                  onClick={() => openCheckout(priceId, tier.name)}
                  variant={tier.highlight ? "default" : "secondary"}
                >
                  {isCurrent
                    ? "Current plan"
                    : !paymentsReady
                      ? "Payments not configured"
                      : `Upgrade to ${tier.name}`}
                </Button>

                {isInstitutional && (
                  <div className="mt-2 rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-400">
                      <Crown className="h-3 w-3" /> Founding Member Offer
                    </div>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <span className="font-display text-xl font-bold">$799</span>
                      <span className="text-[11px] text-muted-foreground">one-time · lifetime</span>
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Lock in forever — only 100 spots.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                      disabled={!paymentsReady}
                      onClick={() => openCheckout("founding_lifetime", "Founding Member Lifetime")}
                    >
                      Claim founding spot
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Partner tier */}
        <div className="mx-auto mt-6 max-w-6xl">
          <Card className="flex flex-col items-start gap-4 border-dashed border-border/60 bg-card/40 p-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-bold">Partner</h3>
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">
                    Coming soon
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  For communities, educators, and prop firms — from <span className="font-semibold text-foreground">$499/month</span>.
                </p>
              </div>
            </div>
            <Button asChild variant="outline">
              <Link to="/support">Join waitlist →</Link>
            </Button>
          </Card>
        </div>

        <div className="mx-auto mt-10 max-w-3xl text-center text-xs text-muted-foreground">
          <p>
            Secure payments. Cancel any time from your{" "}
            <Link to="/profile" className="text-primary hover:underline">
              billing settings
            </Link>
            .
          </p>
        </div>
      </div>

      {checkoutPriceId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/80 p-4 backdrop-blur-sm"
          onClick={closeCheckout}
        >
          <div
            className="mt-8 w-full max-w-2xl rounded-xl border border-border/60 bg-card p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-bold">
                {disclaimerAccepted ? "Complete your upgrade" : "Before you continue"}
              </h3>
              <button
                onClick={closeCheckout}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
            {disclaimerAccepted ? (
              <StripeEmbeddedCheckoutForm priceId={checkoutPriceId} />
            ) : (
              <CheckoutDisclaimerGate
                plan={checkoutPlan}
                priceId={checkoutPriceId}
                onAccepted={() => setDisclaimerAccepted(true)}
                onCancel={closeCheckout}
              />
            )}
          </div>
        </div>
      )}
    </main>
  );
}

function FreeCard({ isCurrent }: { isCurrent: boolean }) {
  return (
    <Card className="flex flex-col gap-4 border-border/60 bg-card/40 p-6">
      <h3 className="font-display text-xl font-bold">Explorer</h3>
      <p className="text-xs text-muted-foreground">Free access to explore the council</p>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-4xl font-bold">$0</span>
        <span className="text-xs text-muted-foreground">/mo</span>
      </div>
      <ul className="space-y-2 text-sm">
        {[
          "3 pairs (EUR/USD, GBP/USD, XAU/USD)",
          "Delayed market intelligence",
          "3 ideas per day",
          "Public performance page",
          "Limited council preview (3 of 11 agents)",
        ].map((perk) => (
          <li key={perk} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">{perk}</span>
          </li>
        ))}
      </ul>
      <Button className="mt-auto w-full" variant="ghost" disabled>
        {isCurrent ? "Current plan" : "Free forever"}
      </Button>
    </Card>
  );
}
